/**
 * Utilisation ratio bar.
 *
 * Always demand / capacity, where > 1.000 fails (CLAUDE.md 5.4). Never inverted,
 * never stored as a percentage -- only ever rendered as a bar in this layer.
 *
 * Two things this gets right that a naive meter does not:
 *
 * 1. **The accessible value is not clamped.** `role="meter"` with `aria-valuemax={1}`
 *    would announce a 1.8 utilisation as "1", i.e. as though the member were exactly
 *    at capacity -- the opposite of the truth, told to precisely the user who cannot
 *    see the red bar. The max tracks the actual ratio, and `aria-valuetext` says it in
 *    words.
 * 2. **The 1.0 threshold is drawn.** The bar saturates visually, so without a marker
 *    at unity a 0.99 and a 1.40 look nearly the same at a glance.
 */
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/cn'
import { formatUtilisation } from '@/lib/format'

export function UtilisationBar({
  ratio,
  label,
  className,
}: {
  ratio: number | null
  label?: string
  className?: string
}) {
  const { t } = useTranslation()

  if (ratio === null || !Number.isFinite(ratio)) return null

  const over = ratio > 1
  // The fill saturates at the full width; the number beside it carries the truth, so
  // a 3.4 utilisation is never hidden behind a merely-full bar.
  const fillPercent = Math.min(ratio, 1) * 100
  const formatted = formatUtilisation(ratio)

  return (
    <div className={cn('flex items-center gap-3', className)}>
      {label && <span className="w-40 shrink-0 truncate text-sm text-text-muted">{label}</span>}

      <div
        className="relative h-2.5 flex-1 overflow-hidden rounded-full bg-[var(--util-bar-track)]"
        role="meter"
        aria-valuenow={Number(ratio.toFixed(3))}
        aria-valuemin={0}
        // Never below the actual value: clamping here would tell a screen-reader user
        // the member is at capacity when it is 80% over.
        aria-valuemax={Math.max(1, Number(ratio.toFixed(3)))}
        aria-valuetext={
          over
            ? t('utilisation.over', { value: formatted })
            : t('utilisation.value', { value: formatted })
        }
        aria-label={label}
      >
        <div
          className="h-full rounded-full transition-[width]"
          style={{
            width: `${fillPercent}%`,
            backgroundColor: over ? 'var(--util-bar-over)' : 'var(--util-bar)',
          }}
        />
        {/* The unity threshold. Only meaningful while the bar is not already full. */}
        {!over && (
          <span aria-hidden className="absolute inset-y-0 end-0 w-px bg-[var(--border-strong)]" />
        )}
      </div>

      <span
        className={cn(
          'tabular w-16 shrink-0 text-end text-sm font-medium',
          over ? 'text-[var(--status-fail)]' : 'text-text',
        )}
      >
        {formatted}
      </span>
    </div>
  )
}
